import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { AlertTriangle, Activity, MapPin } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'

interface SafetyEvent {
  id: string
  event_type: 'harsh_braking' | 'speeding' | 'rapid_acceleration' | 'hard_cornering' | 'distracted_driving'
  severity: 'low' | 'medium' | 'high' | 'critical'
  driver_id: string
  driver_name: string
  vehicle_id: string
  vehicle_name: string
  timestamp: number
  location: string
  speed: number
  details: string
}

const eventTypeConfig = {
  harsh_braking: { label: 'Harsh Braking', icon: AlertTriangle, color: 'text-red-500' },
  speeding: { label: 'Speeding', icon: Activity, color: 'text-orange-500' },
  rapid_acceleration: { label: 'Rapid Acceleration', icon: Activity, color: 'text-yellow-500' },
  hard_cornering: { label: 'Hard Cornering', icon: Activity, color: 'text-blue-500' },
  distracted_driving: { label: 'Distracted Driving', icon: AlertTriangle, color: 'text-purple-500' },
}

const severityConfig = {
  low: { variant: 'secondary' as const, label: 'Low' },
  medium: { variant: 'secondary' as const, label: 'Medium' },
  high: { variant: 'destructive' as const, label: 'High' },
  critical: { variant: 'destructive' as const, label: 'Critical' },
}

export function SafetyEvents() {
  const [eventFilter, setEventFilter] = useState<string>('all')
  const [severityFilter, setSeverityFilter] = useState<string>('all')

  const { data, isLoading } = useQuery({
    queryKey: ['safety-events', eventFilter, severityFilter],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (eventFilter !== 'all') params.set('type', eventFilter)
      if (severityFilter !== 'all') params.set('severity', severityFilter)

      const res = await fetch(`/api/safety/events?${params}`)
      if (!res.ok) throw new Error('Failed to fetch safety events')
      return res.json() as Promise<{ events: SafetyEvent[] }>
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  })

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Recent Safety Events
          </CardTitle>
          <div className="flex gap-2">
            <Select value={eventFilter} onValueChange={setEventFilter}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Events</SelectItem>
                <SelectItem value="harsh_braking">Harsh Braking</SelectItem>
                <SelectItem value="speeding">Speeding</SelectItem>
                <SelectItem value="rapid_acceleration">Rapid Accel.</SelectItem>
                <SelectItem value="hard_cornering">Hard Cornering</SelectItem>
              </SelectContent>
            </Select>
            <Select value={severityFilter} onValueChange={setSeverityFilter}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severity</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">
            Loading events...
          </div>
        ) : !data?.events || data.events.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No safety events found
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Event Type</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Driver</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.events.map((event) => {
                  const typeConfig = eventTypeConfig[event.event_type]
                  const sevConfig = severityConfig[event.severity]
                  const Icon = typeConfig.icon

                  return (
                    <TableRow key={event.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Icon className={`h-4 w-4 ${typeConfig.color}`} />
                          <span className="text-sm">{typeConfig.label}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={sevConfig.variant}>
                          {sevConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium text-sm">
                        {event.driver_name}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {event.vehicle_name}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDistanceToNow(new Date(event.timestamp), { addSuffix: true })}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                        <div className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {event.location}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[250px] truncate">
                        {event.details}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
